import { useNavigate } from 'react-router-dom';
import { useBillingTranslation } from '@/i18n/hooks/useTypedTranslation';
import { useCreditContext } from '@/contexts/CreditContext';

interface CreditBalanceWidgetProps {
  /** Compact mode for header display */
  compact?: boolean;
  /** Show detailed breakdown */
  showBreakdown?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Credit balance display widget for header or dashboard
 * Shows total credits with color coding based on balance health
 */
export function CreditBalanceWidget({
  compact = true,
  showBreakdown = false,
  className = '',
}: CreditBalanceWidgetProps) {
  const { t } = useBillingTranslation();
  const navigate = useNavigate();
  const { balance, loading, error } = useCreditContext();

  console.log('[CreditBalanceWidget] Render - balance:', balance?.totalCredits, 'loading:', loading);

  const getBalanceColor = (): string => {
    if (!balance) return 'text-gray-500';
    const total = balance.totalCredits;
    if (total > 100) return 'text-green-600';
    if (total > 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getBalanceBgColor = (): string => {
    if (!balance) return 'bg-gray-100';
    const total = balance.totalCredits;
    if (total > 100) return 'bg-green-50 hover:bg-green-100';
    if (total > 20) return 'bg-yellow-50 hover:bg-yellow-100';
    return 'bg-red-50 hover:bg-red-100';
  };

  const handleClick = () => {
    navigate('/billing');
  };

  if (loading) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 ${className}`}>
        <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
        {!compact && <span className="text-sm text-gray-500">Loading...</span>}
      </div>
    );
  }

  if (error || !balance) {
    return (
      <button
        onClick={handleClick}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors ${className}`}
      >
        <CreditIcon className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500">--</span>
      </button>
    );
  }

  if (compact) {
    return (
      <button
        onClick={handleClick}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors cursor-pointer ${getBalanceBgColor()} ${className}`}
        title={t('credits.balance')}
      >
        <CreditIcon className={`w-4 h-4 ${getBalanceColor()}`} />
        <span className={`text-sm font-medium ${getBalanceColor()}`}>
          {balance.totalCredits.toLocaleString()}
        </span>
      </button>
    );
  }

  return (
    <div
      className={`p-4 rounded-lg border ${getBalanceBgColor()} cursor-pointer ${className}`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">{t('credits.balance')}</h3>
        <CreditIcon className={`w-5 h-5 ${getBalanceColor()}`} />
      </div>

      <div className={`text-2xl font-bold ${getBalanceColor()}`}>
        {balance.totalCredits.toLocaleString()}
      </div>
      <div className="text-xs text-gray-500 mt-1">{t('credits.totalCredits')}</div>

      {showBreakdown && (
        <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
          <BalanceRow
            label={t('credits.subscriptionCredits')}
            value={balance.subscriptionCredits}
            expireDate={balance.subscriptionCreditsExpireAt}
            t={t}
          />
          <BalanceRow
            label={t('credits.purchasedCredits')}
            value={balance.purchasedCredits}
            expireDate={balance.purchasedCreditsExpireAt}
            t={t}
          />
          <BalanceRow
            label={t('credits.bonusCredits')}
            value={balance.bonusCredits}
            expireDate={balance.bonusCreditsExpireAt}
            t={t}
          />
        </div>
      )}

      {balance.totalCredits < 20 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate('/billing/credits');
            }}
            className="w-full text-sm text-center text-blue-600 hover:text-blue-700 font-medium"
          >
            {t('credits.buyMore')}
          </button>
        </div>
      )}
    </div>
  );
}

interface BalanceRowProps {
  label: string;
  value: number;
  expireDate?: string;
  t: (key: string, params?: Record<string, unknown>) => string;
}

function BalanceRow({ label, value, expireDate, t }: BalanceRowProps) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <div className="text-right">
        <span className="font-medium">{value.toLocaleString()}</span>
        {expireDate && (
          <div className="text-xs text-gray-400">
            {t('credits.expiresOn', { date: formatDate(expireDate) })}
          </div>
        )}
      </div>
    </div>
  );
}

function CreditIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

export default CreditBalanceWidget;
